<template>
  <div>
    <toast-success
      v-if="loading.success"
      label="Scanned Record Saved"
      message="The scanned record has been saved on the server."
      v-on:close="closeSuccess"
    ></toast-success>
    <!-- The form -->
    <div class="form-section">
      <label class="font-bold">Title</label>
      <div class="section-body row u-no-padding">
        <div class="col-12">
          <input
            type="text"
            v-bind:value="data.title"
            v-on:input="$emit('input', 'title', $event.target.value)"
            v-bind:class="{ 'input-error': error.title }"
          >
          <span class="error">{{ error.title }}</span>
        </div>
      </div>
    </div>
    <div class="form-section">
      <label class="font-bold">Candidate</label>
      <div class="section-body row u-no-padding">
        <div class="col-12">
          <multiselect
            v-bind:value="data.candidate"
            v-on:input="$emit('input', 'candidate', {...$event})"
            :options="users"
            :custom-label="getPilotLabel"
            :show-labels="false"
            track-by="id"
            :close-on-select="true"
            v-bind:class="{ 'input-error': error.candidate }"
            ></multiselect>
          <span class="error">{{ error.candidate }}</span>
        </div>
      </div>
    </div>

    <div class="form-section">
      <label class="font-bold">Record Type</label>
      <div class="section-body row u-no-padding">
        <div class="col-12">
          <multiselect
            v-bind:value="data.record_type"
            v-on:input="$emit('input', 'record_type', $event)"
            :options="scannedRecordTypes"
            label="label"
            :show-labels="false"
            track-by="id"
            :close-on-select="true"
            v-bind:class="{ 'input-error': error.record_type }"
            ></multiselect>
          <span class="error">{{ error.record_type }}</span>
        </div>
      </div>
    </div>

    <div class="form-section">
      <label class="font-bold">Tags</label>
      <div class="section-body row u-no-padding">
        <div class="col-12">
          <multiselect
            v-bind:value="data.tags"
            v-on:input="$emit('input', 'tags', $event)"
            :options="scannedRecordTags"
            :multiple="true"
            :close-on-select="false"
            label="label"
            :show-labels="false"
            track-by="id"
            v-bind:class="{ 'input-error': error.tags }"
            ></multiselect>
          <span class="error">{{ error.tags }}</span>
        </div>
      </div>
    </div>

    <div class="form-section">
      <div class="section-body row u-no-padding level-content">
        <div class="col-12 font-bold">Document</div>
        <div class="col-2 level-item">
          <button
            class="btn-small" style="margin: 0.3em 2px;"
            v-bind:class="{ 'input-error': error.document }"
          >
            <label for="fileUpload">
              <i
                class="fa-wrapper fa fa-upload pad-right pad-left"
                aria-hidden="true"
              ></i>
              <span v-if="!data.document">Select</span>
              <span v-else>Change</span>
            </label>
          </button>
          <input
            id="fileUpload"
            type="file"
            :disabled="loading.Records"
            v-on:input="$emit('input', 'document', $event.target.files[0])"
          >
        </div>
        <div class="col-8 u-text-left level-item">
          <div
            v-if="data.document && data.document.name"
            class="u-fill-width"
          >
            File: {{ data.document.name }}
            ({{ Math.round(data.document.size / 1024) }} kb)
            <div class="error">{{ error.document }}</div>
          </div>
          <div v-else class="u-fill-width">
            Current File:
            <a v-bind:href="data.document">{{ data.document }}</a>
            <span class="error">{{ error.document }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="panel">
      <button class="btn-primary" v-on:click="save">
        <loading-spinner v-show="loading.Records"></loading-spinner>
        <i class="fa-wrapper fa fa-upload pad-right" v-show="!loading.Records"></i>
        {{ actionButtonLabel }}
      </button>
    </div>
    <div v-if="$route.name == 'scanned-record-update'">
      <space></space>
      <div class="toast">
        <h6>Note:</h6>
        <p>
          When a <em>scanned record</em> is edited the version and modified
          date is updated, but <em>not historical version is retained on the
          server.</em>  If you're <em>updating</em> an old record with a new
          one you should leave the old one in place and <em>create a new record
          for the new document</em>.
        </p>
      </div>
      <space></space>
    </div>
    <div class="panel u-box" style="background: #f6f8fa;">
      <h6 class="font-alt font-light warning">
        Debug: Selected Candidate Details
      </h6>
      <div class="row" v-for="(val, key, idx) in data.candidate" v-bind:key="idx">
        <div class="col-2 strong">{{ key }}:</div>
        <div class="col-10">{{ val }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ToastSuccess from "@/components/ToastSuccess";
import Multiselect from "vue-multiselect";
import LoadingSpinner from "@/components/LoadingSpinner";
import { getPilotLabel } from "@/functions/getPilotLabel";
import { debounce } from "@/functions/debounce.function";
import { clearFormFieldErrors } from "@/functions/clearFormFieldErrors";

export default {
  name: "scanned-record-form",
  props: ["data"],
  components: {
    ToastSuccess,
    Multiselect,
    LoadingSpinner,
  },
  computed: {
    ...mapGetters("auth", ["users", "userProfile", "permissions"]),
    ...mapGetters("core", ["error"]),
    ...mapGetters("records", ["loading", "scannedRecordTypes", "scannedRecordTags"]),
    actionButtonLabel: function() {
      return this.$route.name.split("-").slice(-1)[0]
    }
  },
  watch: {
    data: {
      // Clear errors on a field when its value is changed
      handler: debounce(function(formFields) {
        clearFormFieldErrors(formFields);
      }, 300),
      deep: true
    },
  },
  methods: {
    getPilotLabel: function(payload) {
      return getPilotLabel(payload);
    },
    validate: function() {
      var err = {};
      var isValid = true;
      // Ensure all required fields are filled out
      let required = ["title", "candidate", "document"]
      for (let field of required) {
        if (this.data[field] == undefined) {
          err[field] = "This field is required";
          isValid = false;
        }
      };
      // Note that file size limits and file type are enforced by the server
      // configuration so aren't included in this validation.
      this.$store.commit("core/setError", err);
      return isValid;
    },
    save: async function() {
      this.saving = true;
      this.$store.commit("core/clearErrors");
      // Validate the form data
      let isValid = this.validate()
      if (!isValid) {
        this.saving = false;
        return
      }
      let cleanedData = new FormData()
      if (this.data.candidate.id) {
        cleanedData.set("candidate", this.data.candidate.id);
      } else {
        // TEMP CODE TO HANDLE `undefined` ERRORS
        let err = {
          serverError: {
            label: "Candidate data missing",
            message: "UNDEFINED CANDIDATE. Please take a screenshot and report this error to admin@taslytic.com Thanks =)"
          }
        };
        this.$store.commit("core/setError", err);
        this.saving = false;
        return
      }
      if (this.data.title) {
        cleanedData.set("title", this.data.title);
      }
      if (this.data.record_type) {
        cleanedData.set("record_type", this.data.record_type.id);
      };
      if (this.data.tags) {
        let stack = []
        for (let tag of this.data.tags) {
          stack.push(tag.id)
        }
        cleanedData.append("tags", stack);
      }
      if (typeof(this.data.document) != "string") {
        // Only send the file if it's changed — if it's not changed it'll be a
        // string (the URL of the existing file).
        cleanedData.append("document", this.data.document);
      }
      // Dispatch different actions depending on whether this is the create or
      // update view.
      if (this.$route.name == "scanned-record-create") {
        this.$store.dispatch("records/createScannedRecord", cleanedData);
      } else if (this.$route.name == "scanned-record-update") {
        let payload = {
          id: this.$route.params.id,
          formData: cleanedData
        }
        this.$store.dispatch("records/updateScannedRecord", payload)
          .then(() => {
            this.saving = false;
          });
      }
    },
    closeSuccess: function() {
      this.$store.commit("records/closeSuccess");
    },
  },
  mounted() {
    this.$store.commit("core/clearErrors");
  }
}
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
